import React, { useState, useEffect } from 'react';
import { MagnifyingGlass, Eye, Clock, Car, User, Phone, EnvelopeSimple, MapPin, Image as ImageIcon, Robot, GlobeHemisphereWest, DeviceMobile } from '@phosphor-icons/react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import Pagination, { usePagination } from '../../components/Pagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAnalyses = () => {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedFull, setSelectedFull] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showReasoning, setShowReasoning] = useState(false);

  useEffect(() => { fetchAnalyses(); }, [statusFilter]);

  const fetchAnalyses = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      const response = await axios.get(`${API_URL}/api/analyses?${params}`);
      setAnalyses(response.data);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  // Derive the unique brand list (from declared OR Gemini-identified brand)
  const allBrands = Array.from(new Set(
    analyses
      .map(a => (a.brand || a.vehicle_identified?.brand || '').trim().toUpperCase())
      .filter(Boolean)
  )).sort();

  // Apply client-side filters (brand + date range) on top of the server status filter
  const filtered = analyses.filter(a => {
    if (brandFilter) {
      const aBrand = ((a.brand || a.vehicle_identified?.brand || '').toUpperCase());
      if (!aBrand.includes(brandFilter.toUpperCase())) return false;
    }
    if (dateFrom) {
      const t = new Date(a.created_at).getTime();
      if (isNaN(t) || t < new Date(dateFrom).getTime()) return false;
    }
    if (dateTo) {
      const t = new Date(a.created_at).getTime();
      // include the whole "to" day → add 24h
      const end = new Date(dateTo).getTime() + 24 * 60 * 60 * 1000;
      if (isNaN(t) || t >= end) return false;
    }
    return true;
  });

  const resetFilters = () => { setStatusFilter(''); setBrandFilter(''); setDateFrom(''); setDateTo(''); };

  const { currentPage, setCurrentPage, paginated, totalPages, totalItems, rangeStart, rangeEnd } = usePagination(filtered, 20);

  const openDetail = async (analysisId) => {
    setDetailLoading(true);
    setShowReasoning(false);
    setDialogOpen(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/analyses/${analysisId}/full`);
      setSelectedFull(response.data);
    } catch (error) {
      console.error('Error loading detail:', error);
      setSelectedFull(null);
    } finally { setDetailLoading(false); }
  };

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  const getStatusBadge = (status) => {
    const styles = { pending: 'bg-amber-100 text-amber-700', analyzing: 'bg-blue-100 text-blue-700', completed: 'bg-[#2A9D8F]/10 text-[#2A9D8F]', validated: 'bg-[#2A9D8F]/10 text-[#2A9D8F]', error: 'bg-[#E63946]/10 text-[#E63946]' };
    const labels = { pending: 'En attente', analyzing: 'En cours', completed: 'Terminée', validated: 'Validée', error: 'Erreur' };
    return <span className={`px-2 py-1 text-xs font-bold uppercase ${styles[status] || styles.pending}`}>{labels[status] || status}</span>;
  };

  const getConfidenceColor = (c) => c >= 80 ? 'text-[#2A9D8F]' : c >= 50 ? 'text-amber-500' : 'text-[#E63946]';

  const parseUA = (ua = '') => {
    if (!ua) return 'Inconnu';
    const browser = ua.match(/(Chrome|Firefox|Safari|Edge|Opera|MSIE)\/[\d.]+/i)?.[0]?.split('/')[0] || 'Navigateur';
    const os = ua.match(/(Windows NT|Mac OS X|Android|iPhone OS|Linux)[ _;\d.]*/i)?.[0]?.trim().replace(/[_]/g, ' ') || '';
    const mobile = /Mobile|Android|iPhone|iPad/i.test(ua);
    return `${browser} · ${os}${mobile ? ' · Mobile' : ' · Desktop'}`;
  };

  return (
    <AdminLayout title="Analyses">
      <div className="bg-white border border-gray-200 p-4 mb-6 grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="analyses-filters">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-white border border-gray-200 text-gray-900 px-3 py-2 text-sm" data-testid="filter-status">
          <option value="">Tous statuts</option>
          <option value="pending">En attente</option>
          <option value="analyzing">En cours</option>
          <option value="completed">Terminée</option>
          <option value="validated">Validée</option>
          <option value="error">Erreur</option>
        </select>
        <select value={brandFilter} onChange={(e) => { setBrandFilter(e.target.value); setCurrentPage(1); }} className="bg-white border border-gray-200 text-gray-900 px-3 py-2 text-sm" data-testid="filter-brand">
          <option value="">Toutes marques</option>
          {allBrands.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <div className="flex flex-col">
          <label className="text-gray-400 text-[10px] uppercase mb-0.5">Du</label>
          <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }} className="bg-white border border-gray-200 text-gray-900 px-3 py-1.5 text-sm" data-testid="filter-date-from" />
        </div>
        <div className="flex flex-col">
          <label className="text-gray-400 text-[10px] uppercase mb-0.5">Au</label>
          <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }} className="bg-white border border-gray-200 text-gray-900 px-3 py-1.5 text-sm" data-testid="filter-date-to" />
        </div>
        <button onClick={resetFilters} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 text-sm font-medium border border-gray-200 transition-colors self-end" data-testid="filter-reset">Réinitialiser</button>
      </div>
      
      <div className="text-gray-500 text-xs mb-3" data-testid="analyses-count">{filtered.length} analyse{filtered.length > 1 ? 's' : ''} {filtered.length !== analyses.length && `(sur ${analyses.length} au total)`}</div>

      <div className="bg-white border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-gray-200 bg-gray-50">
              <th className="text-left p-4 text-gray-500 text-xs uppercase">Date</th>
              <th className="text-left p-4 text-gray-500 text-xs uppercase">Véhicule</th>
              <th className="text-left p-4 text-gray-500 text-xs uppercase">Images</th>
              <th className="text-left p-4 text-gray-500 text-xs uppercase">Pièces</th>
              <th className="text-left p-4 text-gray-500 text-xs uppercase">Statut</th>
              <th className="text-right p-4 text-gray-500 text-xs uppercase">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? <tr><td colSpan={6} className="p-8 text-center"><div className="w-6 h-6 border-2 border-[#E63946] border-t-transparent rounded-full animate-spin mx-auto"></div></td></tr>
              : paginated.length > 0 ? paginated.map((a, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="p-4 text-gray-500 text-sm"><div className="flex items-center gap-2"><Clock size={14} className="text-gray-400" />{formatDate(a.created_at)}</div></td>
                  <td className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-gray-100 border border-gray-200 flex items-center justify-center"><Car size={20} className="text-gray-300" /></div><div className="min-w-0">
                    <p className="text-gray-900 font-medium truncate">{a.brand || a.vehicle_identified?.brand || 'Véhicule'} {a.model || a.vehicle_identified?.model || ''}</p>
                    <p className="text-gray-400 text-xs truncate">{a.year || a.vehicle_identified?.year || ''} {a.registration && `• ${a.registration}`}</p>
                    {a.vehicle_identified?.brand && (a.brand || a.model) && (a.vehicle_identified.brand !== (a.brand || '').toUpperCase().split(' ')[0]?.toUpperCase()) && (
                      <p className="text-[#2A9D8F] text-[10px] truncate flex items-center gap-1" data-testid={`gemini-vehicle-${i}`}><Robot size={9} weight="bold" />Gemini: {a.vehicle_identified.brand} {a.vehicle_identified.model || ''}</p>
                    )}
                  </div></div></td>
                  <td className="p-4 text-gray-600">{a.image_paths?.length || 0} photos</td>
                  <td className="p-4 text-gray-900 font-medium">{a.detected_parts?.length || 0}</td>
                  <td className="p-4">{getStatusBadge(a.status)}</td>
                  <td className="p-4"><button onClick={() => openDetail(a.id)} className="p-2 text-gray-400 hover:text-[#E63946] hover:bg-gray-100 flex items-center gap-2 ml-auto" data-testid={`view-analysis-${i}`}><Eye size={16} />Détails</button></td>
                </tr>
              )) : <tr><td colSpan={6} className="p-8 text-center"><MagnifyingGlass size={48} className="text-gray-300 mx-auto mb-4" /><p className="text-gray-400">Aucune analyse trouvée</p></td></tr>}
            </tbody>
          </table>
        </div>
        {!loading && (
          <div className="px-4 pb-2">
            <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setCurrentPage} rangeStart={rangeStart} rangeEnd={rangeEnd} totalItems={totalItems} />
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="analysis-detail-dialog">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-xl">{`Détails de l'analyse`}</DialogTitle></DialogHeader>
          {detailLoading ? (
            <div className="py-12 flex justify-center"><div className="w-8 h-8 border-2 border-[#E63946] border-t-transparent rounded-full animate-spin"></div></div>
          ) : selectedFull ? (
            <div className="space-y-6 mt-4">
              {/* Banner client info if quote exists */}
              {selectedFull.has_quote && selectedFull.linked_quotes?.[0] && (
                <div className="bg-[#E63946]/5 border border-[#E63946]/30 p-4" data-testid="client-info-panel">
                  <h4 className="text-[#E63946] text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2"><User size={14} weight="bold" />Client identifié (devis demandé)</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div><span className="text-gray-400 text-xs">Nom</span><p className="text-gray-900 font-medium">{selectedFull.linked_quotes[0].first_name} {selectedFull.linked_quotes[0].last_name}</p></div>
                    <div><span className="text-gray-400 text-xs flex items-center gap-1"><EnvelopeSimple size={11} />Email</span><p className="text-gray-900 truncate">{selectedFull.linked_quotes[0].email}</p></div>
                    <div><span className="text-gray-400 text-xs flex items-center gap-1"><Phone size={11} />Téléphone</span><p className="text-gray-900">{selectedFull.linked_quotes[0].phone}</p></div>
                    <div><span className="text-gray-400 text-xs flex items-center gap-1"><MapPin size={11} />Ville</span><p className="text-gray-900">{selectedFull.linked_quotes[0].city}</p></div>
                  </div>
                  {selectedFull.linked_quotes[0].message && (
                    <div className="mt-3 pt-3 border-t border-[#E63946]/20"><span className="text-gray-400 text-xs">Message du client</span><p className="text-gray-700 text-sm italic">{`"${selectedFull.linked_quotes[0].message}"`}</p></div>
                  )}
                  {selectedFull.linked_quotes.length > 1 && (
                    <p className="text-gray-500 text-xs mt-2">+ {selectedFull.linked_quotes.length - 1} autre(s) devis pour cette analyse</p>
                  )}
                </div>
              )}

              {/* Anonymous fingerprint */}
              <div className="bg-gray-50 border border-gray-200 p-4">
                <h4 className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2"><GlobeHemisphereWest size={14} />Empreinte utilisateur</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div><span className="text-gray-400 text-xs">IP</span><p className="text-gray-900 font-mono text-xs" data-testid="client-ip">{selectedFull.analysis.client_ip || '—'}</p></div>
                  <div className="md:col-span-2"><span className="text-gray-400 text-xs flex items-center gap-1"><DeviceMobile size={11} />Navigateur / OS</span><p className="text-gray-900 text-xs">{parseUA(selectedFull.analysis.client_user_agent)}</p></div>
                </div>
              </div>

              {/* Vehicle — declared vs Gemini-identified side by side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-gray-50 border border-gray-200 p-4">
                  <h4 className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2"><Car size={14} />Véhicule déclaré par le client</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-400 text-xs">Marque</span><p className="text-gray-900">{selectedFull.analysis.brand || '—'}</p></div>
                    <div><span className="text-gray-400 text-xs">Modèle</span><p className="text-gray-900">{selectedFull.analysis.model || '—'}</p></div>
                    <div><span className="text-gray-400 text-xs">Année</span><p className="text-gray-900">{selectedFull.analysis.year || '—'}</p></div>
                    <div><span className="text-gray-400 text-xs">Immat.</span><p className="text-gray-900 font-mono text-xs">{selectedFull.analysis.registration || '—'}</p></div>
                    <div className="col-span-2"><span className="text-gray-400 text-xs">VIN</span><p className="text-gray-900 font-mono text-xs break-all">{selectedFull.analysis.vin || '—'}</p></div>
                  </div>
                </div>
                <div className="bg-[#2A9D8F]/5 border border-[#2A9D8F]/30 p-4" data-testid="gemini-vehicle-panel">
                  <h4 className="text-[#2A9D8F] text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2"><Robot size={14} weight="bold" />Véhicule identifié par Gemini</h4>
                  {selectedFull.analysis.vehicle_identified ? (
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-gray-400 text-xs">Marque</span><p className="text-gray-900 font-medium" data-testid="gemini-brand">{selectedFull.analysis.vehicle_identified.brand || '—'}</p></div>
                      <div><span className="text-gray-400 text-xs">Modèle</span><p className="text-gray-900 font-medium" data-testid="gemini-model">{selectedFull.analysis.vehicle_identified.model || '—'}</p></div>
                      <div><span className="text-gray-400 text-xs">Année</span><p className="text-gray-900 font-medium" data-testid="gemini-year">{selectedFull.analysis.vehicle_identified.year || '—'}</p></div>
                      <div><span className="text-gray-400 text-xs">Couleur</span><p className="text-gray-900">{selectedFull.analysis.vehicle_identified.color || '—'}</p></div>
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm italic">{`Gemini n'a pas identifié le véhicule`}</p>
                  )}
                </div>
              </div>
              {selectedFull.analysis.comment && <div className="bg-gray-50 border border-gray-200 p-3"><span className="text-gray-400 text-xs">Commentaire client</span><p className="text-gray-700 text-sm">{selectedFull.analysis.comment}</p></div>}

              {/* Photos */}
              {selectedFull.image_urls?.length > 0 && (
                <div data-testid="analysis-photos-panel">
                  <h4 className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2"><ImageIcon size={14} />{`Photos envoyées par l'utilisateur (${selectedFull.image_urls.length})`}</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {selectedFull.image_urls.map((url, i) => (
                      <a key={i} href={`${API_URL}${url}`} target="_blank" rel="noopener noreferrer" className="block bg-gray-50 border border-gray-200 hover:border-[#E63946] transition-colors aspect-[4/3] overflow-hidden" data-testid={`analysis-photo-${i}`}>
                        <img src={`${API_URL}${url}`} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-gray-300 text-xs">Image indisponible</div>'; }} />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Damage zones */}
              {selectedFull.analysis.damage_zones?.length > 0 && (
                <div>
                  <h4 className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-3">Zones de dommages détectées</h4>
                  <div className="flex flex-wrap gap-2">{selectedFull.analysis.damage_zones.map((z, i) => <span key={i} className="px-3 py-1.5 bg-[#E63946]/10 border border-[#E63946]/30 text-[#E63946] text-sm">{z}</span>)}</div>
                </div>
              )}

              {/* Detected parts with stock matches */}
              <div data-testid="detected-parts-panel">
                <h4 className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-3">Pièces identifiées par Gemini ({selectedFull.analysis.detected_parts?.length || 0})</h4>
                <div className="space-y-3">
                  {selectedFull.analysis.detected_parts?.map((p, i) => (
                    <div key={i} className="bg-white border border-gray-200 p-4" data-testid={`detected-part-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex-1">
                          <p className="text-gray-900 font-semibold">{p.part_name}</p>
                          <p className="text-gray-400 text-xs">{p.category} {p.side && `• ${p.side}`} {p.oem_reference && `• Réf: ${p.oem_reference}`}</p>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {(p.estimated_market_price || p.price) && <span className="text-[#2A9D8F] font-bold text-sm">{(p.estimated_market_price || p.price).toFixed(2)} €</span>}
                          <span className={`font-bold text-sm ${getConfidenceColor(p.confidence)}`}>{p.confidence}%</span>
                        </div>
                      </div>
                      {p.damage_description && <p className="text-gray-600 text-xs italic mb-2">{`"${p.damage_description}"`}</p>}
                      {/* Stock matches */}
                      {p.stock_ecocasse?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-[#E63946] text-xs font-bold uppercase mb-2">Stock Ecocasse matché ({p.stock_ecocasse.length})</p>
                          <div className="space-y-1.5">
                            {p.stock_ecocasse.slice(0, 3).map((s, si) => (
                              <div key={si} className="flex items-center gap-2 text-xs bg-[#E63946]/5 border border-[#E63946]/20 px-2 py-1.5">
                                {s.vignette && <img src={s.vignette} alt="" className="w-8 h-8 object-cover border border-gray-200" onError={(e) => e.target.style.display = 'none'} />}
                                <div className="flex-1 min-w-0">
                                  <p className="text-gray-900 font-medium truncate">{s.nom}</p>
                                  <p className="text-gray-400 text-[10px] truncate">{s.marque} {s.modele} {s.ref_oem && `· ${s.ref_oem}`}</p>
                                </div>
                                <span className="text-[#E63946] font-bold">{Number(s.prix_ttc || 0).toFixed(2)} €</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Gemini raw reasoning (collapsible) */}
              {selectedFull.analysis.ai_raw_response && (
                <div>
                  <button onClick={() => setShowReasoning(!showReasoning)} className="w-full bg-gray-50 hover:bg-gray-100 border border-gray-200 p-3 flex items-center justify-between text-sm transition-colors" data-testid="toggle-gemini-reasoning">
                    <span className="flex items-center gap-2 font-medium text-gray-700"><Robot size={16} weight="bold" />Raisonnement brut de Gemini ({selectedFull.analysis.ai_raw_response.length.toLocaleString()} car.)</span>
                    <span className="text-gray-400 text-xs">{showReasoning ? 'Masquer' : 'Afficher'}</span>
                  </button>
                  {showReasoning && (
                    <pre className="mt-2 bg-gray-900 text-gray-100 p-4 text-[11px] leading-relaxed font-mono whitespace-pre-wrap max-h-[400px] overflow-y-auto" data-testid="gemini-reasoning-content">{selectedFull.analysis.ai_raw_response}</pre>
                  )}
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-8">Impossible de charger les détails.</p>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminAnalyses;
